import { useNavigate } from "react-router";

import { t } from "i18next";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Box, Button, Grid, Typography } from "@mui/material";

import { isDarkMode } from "@/src/theme/helpers";
import { schemeSx } from "@/src/theme/schemeStyles";

import { articleFilenameToSlugAtom } from "@/src/state/articleFilenameToSlugAtom";
import { articleListAtom } from "@/src/state/articleListAtom";
import { articlesAtom } from "@/src/state/articlesAtom";

import { dashesToUnderscores } from "@/src/pages/consumer/BlogPage/BlogPage";
import { ArticleCard } from "@/src/pages/consumer/BlogPage/components/ArticleCard";
import { ArticleCardSkeleton } from "@/src/pages/consumer/BlogPage/components/ArticleCardSkeleton";
import {
  HOMEPAGE_FRONT_PAGE_CONTAINER_STYLES,
  HOMEPAGE_SECTION_TITLE_SX
} from "@/src/pages/consumer/HomePage/homePage.constants";

import { useIsHydrating } from "@/src/hooks/useIsHydrating";
import { articlesFromFilenameList } from "@/src/utils/articleSyncHelpers";
import {
  type Article,
  getArticleUrl,
  useGetArticles
} from "@/src/utils/blogUtils";

import { useGTMDispatch } from "@elgorditosalsero/react-gtm-hook";
import { useAtomValue } from "jotai";

export interface HomeArticleData {
  filenames: string[];
  filenameToSlug: Record<string, string>;
  articles: Record<string, Article>;
}

interface HomePageArticleBlockProps {
  /** Build-time articles from the route loader, shown in the prerendered HTML. */
  initialArticles?: HomeArticleData;
}

const HomePageArticleBlock = ({
  initialArticles
}: HomePageArticleBlockProps) => {
  const sendDataToGTM = useGTMDispatch();
  const navigate = useNavigate();
  const filenames = useAtomValue(articleListAtom);
  const filenameToSlug = useAtomValue(articleFilenameToSlugAtom);
  const articles = useAtomValue(articlesAtom);

  const { isLoading } = useGetArticles();
  // The article atoms are persisted in localStorage and read on init, so on
  // the client they can differ from the prerendered HTML. While hydrating, use
  // the loader data the HTML was built from; afterwards use the atoms only once
  // they are at least as new as the loader data (an old cache stays in place
  // until the sync refreshes it).
  const isHydrating = useIsHydrating();
  const initialList = initialArticles
    ? articlesFromFilenameList(
        initialArticles.filenames,
        initialArticles.filenameToSlug,
        initialArticles.articles
      )
    : [];
  const atomList = articlesFromFilenameList(
    filenames,
    filenameToSlug,
    articles
  );
  const atomsAreCurrent =
    atomList.length > 0 &&
    (initialList.length === 0 || atomList[0].date >= initialList[0].date);
  const useInitial =
    initialList.length > 0 && (isHydrating || !atomsAreCurrent);
  const articleList = (useInitial ? initialList : atomList).slice(0, 4);

  const handleSendGtmEvent = (gtmEvent: string) => {
    sendDataToGTM({
      event: { gtmEvent },
      user_type: "consumer"
    });
  };

  // Prerendered cards stay visible while the sync runs.
  const showSkeletons = isLoading && articleList.length === 0;

  if (!isLoading && articleList.length === 0) {
    return null;
  }

  const articleCards = (
    <Grid
      container
      sx={{
        // Responsive sx values instead of useMediaQuery, which is false in the
        // prerendered HTML and changes the layout after hydration.
        maxWidth: { xs: 1000, lg: "none" },
        // ArticleCard has its own 16px side padding; on the four-column layout
        // pull the row out by that much so the cards line up with the heading.
        width: { lg: "calc(100% + 32px)" },
        mx: { xs: "auto", lg: -2 }
      }}
    >
      {showSkeletons
        ? [1, 2, 3, 4].map((i) => (
            <Grid
              key={`skeleton-${i}`}
              size={{
                xs: 12,
                sm: 6,
                lg: 3
              }}
            >
              <ArticleCardSkeleton />
            </Grid>
          ))
        : articleList.map((article) => (
            <Grid
              key={`blog-article-card-grid-${article.date}`}
              size={{
                xs: 12,
                sm: 6,
                lg: 3
              }}
            >
              <ArticleCard
                to={`/blog/${getArticleUrl(article.title)}`}
                articleData={article}
                titleSize="medium"
                onClick={() => {
                  handleSendGtmEvent(
                    `article_card_${dashesToUnderscores(article.date)}_clicked`
                  );
                }}
              />
            </Grid>
          ))}
    </Grid>
  );

  return (
    <Box
      sx={schemeSx((muiTheme) => ({
        ...HOMEPAGE_FRONT_PAGE_CONTAINER_STYLES,
        background: muiTheme.palette.misc.timelineBackground
      }))}
    >
      <Typography variant="h2" sx={HOMEPAGE_SECTION_TITLE_SX}>
        {t("consumer.homePage.articleBlockTitle")}
      </Typography>
      {articleCards}
      {!showSkeletons && (
        <Box
          sx={{
            display: "flex",
            justifyContent: { xs: "center", md: "flex-end" },
            // Adds to the card's own 16px bottom margin.
            mt: { lg: 1 }
          }}
        >
          <Button
            variant="text"
            sx={schemeSx((muiTheme) =>
              isDarkMode(muiTheme)
                ? { color: muiTheme.palette.secondary.main }
                : {}
            )}
            onClick={() => {
              handleSendGtmEvent("show_more_articles_clicked");
              navigate("/blog");
            }}
            endIcon={<ArrowForwardIcon />}
          >
            {t("general.showMore")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default HomePageArticleBlock;
